import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AdminSection } from "./admin";
import { Input, TableShell } from "@/components/admin/AdminUI";
import { listAllCustomers } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/customers")({
  component: CustomersAdmin,
});

type Row = Awaited<ReturnType<typeof listAllCustomers>>[number];

function money(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

function CustomersAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await listAllCustomers();
        setRows(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        (r.display_name ?? "").toLowerCase().includes(needle) ||
        r.id.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const members = rows.filter((r) => r.membership_tier).length;
  const buyers = rows.filter((r) => r.orders > 0).length;

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Customers" value={rows.length} />
        <Stat label="Members" value={members} tone="text-emerald-400" />
        <Stat label="Buyers" value={buyers} />
      </div>

      <AdminSection
        title="Customer directory"
        description="All accounts registered on the site. Search by display name or id."
      >
        <div className="mb-4 max-w-sm">
          <Input placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No customers match.</p>
        ) : (
          <TableShell
            headers={["Customer", "Membership", "Orders", "Spend", "Points", "Joined"]}
            rows={filtered.map((r) => (
              <tr key={r.id} className="border-t hairline">
                <td className="px-3 py-2 text-sm">
                  <div className="font-medium">{r.display_name || "—"}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.id.slice(0, 8)}…</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.membership_tier ? (
                    <span className="rounded-full border hairline px-2 py-0.5 text-emerald-400">{r.membership_tier}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-sm">{r.orders}</td>
                <td className="px-3 py-2 text-sm">{money(r.spend_cents)}</td>
                <td className="px-3 py-2 text-sm">{r.reward_points}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          />
        )}
      </AdminSection>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className={`mt-3 font-serif text-4xl ${tone ?? "text-gold-luminous"}`}>{value}</p>
    </div>
  );
}
